import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { user as userApi, sessions } from '../../services/api';
import JitsiMeeting from './JitsiMeeting';
import './VideoMeetingPage.css';

/**
 * VideoMeetingPage Component
 *
 * Landing page for creating/joining video meetings
 * Requires languageId query parameter
 */
const VideoMeetingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [meetingUrl, setMeetingUrl] = useState('');
  const [meetingId, setMeetingId] = useState('');
  const [jwtToken, setJwtToken] = useState('');
  const [language, setLanguage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [error, setError] = useState('');
  const [showMeeting, setShowMeeting] = useState(false);

  const languageId = searchParams.get('languageId');

  useEffect(() => {
    if (!languageId) {
      setError('No language selected. Please select a language from the home page.');
      return;
    }

    // Check if there's an existing session
    const existingMeetingId = sessionStorage.getItem('currentMeetingId');

    if (!existingMeetingId) {
      // No existing session - create a new one
      console.log('📝 No existing session found - Creating NEW meeting session for languageId:', languageId);
      createMeeting();
    } else {
      // Session exists - load room details but don't create new session
      console.log('♻️ Existing session found:', existingMeetingId, '- Reusing it');
      // We still need to fetch room details to display the meeting
      setIsLoading(true);
      userApi.getVideoMeetingRoom(languageId)
        .then(response => {
          const { roomId, hasAccess: access, token, language: lang } = response.data;
          setMeetingId(roomId);
          setJwtToken(token || '');
          setLanguage(lang);
          const appUrl = window.location.origin;
          const shareableUrl = `${appUrl}/meet/${roomId}`;
          setMeetingUrl(shareableUrl);
          setHasAccess(access);
          if (access) {
            setShowMeeting(true);
          }
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Failed to fetch meeting room:', err);
          setError('Failed to create meeting. Please try again.');
          setIsLoading(false);
        });
    }
  }, [languageId]);

  const createMeeting = async () => {
    if (!languageId) {
      setError('No language selected. Please select a language from the home page.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Fetch persistent meeting room from backend with language context
      const response = await userApi.getVideoMeetingRoom(languageId);
      const { roomId, hasAccess: access, token, language: lang } = response.data;

      setMeetingId(roomId);
      setJwtToken(token || '');
      setLanguage(lang);

      // Generate shareable URL that points to our app, not directly to Jitsi
      // Guests will access via /meet/:roomId route
      const appUrl = window.location.origin;
      const shareableUrl = `${appUrl}/meet/${roomId}`;
      setMeetingUrl(shareableUrl);
      setHasAccess(access);

      if (!access) {
        setError('Your trial has expired. Please subscribe to access video meetings.');
      } else {
        // Create session record to track this meeting
        try {
          const sessionResponse = await sessions.startSession({
            roomId: roomId,        // Persistent room identifier (e.g., user-abc12345-es)
            languageId: languageId,
          });
          const meetingId = sessionResponse.data.id;  // UUID for this specific meeting session
          console.log('✅ Meeting session created - meetingId:', meetingId, 'for room:', roomId);

          // Store meeting ID (unique per session) and room ID (persistent)
          sessionStorage.setItem('currentMeetingId', meetingId);  // UUID for this specific meeting session
          sessionStorage.setItem('currentRoomId', roomId);         // Persistent room identifier (user-abc12345-es)
        } catch (err) {
          console.error('⚠️ Failed to create session record:', err);
          // Don't block meeting - session tracking is optional
        }

        // Auto-join meeting after successful creation
        setShowMeeting(true);
      }
    } catch (err) {
      console.error('Failed to fetch meeting room:', err);
      if (err.response?.status === 400) {
        setError(err.response.data.error || 'Invalid language selected.');
      } else if (err.response?.status === 403) {
        setError('Access denied to this language.');
      } else {
        setError('Failed to create meeting. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const joinMeeting = () => {
    if (meetingId && hasAccess) {
      // Show embedded meeting on the same page
      setShowMeeting(true);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(meetingUrl);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 3000);
  };

  const handleLeaveMeeting = () => {
    // This page is only for authenticated paying users
    // Redirect to language-specific progress page after leaving meeting
    const progressUrl = languageId ? `/progress?languageId=${languageId}` : '/progress';
    console.log(`🚪 VideoMeetingPage: User left meeting, redirecting to ${progressUrl}`);

    // Clear session storage to allow creating new session next time
    sessionStorage.removeItem('currentMeetingId');
    sessionStorage.removeItem('currentRoomId');
    console.log('🧹 Cleared session storage for next meeting');

    navigate(progressUrl);
  };

  return (
    <div className="video-meeting-page">
      {showMeeting ? (
        // Embedded meeting view
        <JitsiMeeting
          meetingId={meetingId}
          roomName={meetingId}
          jwtToken={jwtToken}
          onLeave={handleLeaveMeeting}
        />
      ) : (
        <>
          <div className="meeting-header">
            <button onClick={() => navigate('/home')} className="btn-back">
              ← Back
            </button>
            <h1>Video Meeting</h1>
          </div>

          <div className="meeting-content">
            {!meetingUrl ? (
          // Pre-meeting state
          <div className="meeting-create">
            <div className="icon-container">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" strokeWidth="2">
                <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>

            <h2>Start a Video Meeting</h2>
            <p className="meeting-description">
              Create an instant video meeting with your study partner.
              <br />
              {language ? (
                <>Practice <strong>{language.name}</strong> together in real-time!</>
              ) : (
                'Practice together in real-time!'
              )}
            </p>

            <button
              onClick={createMeeting}
              disabled={isLoading}
              className="btn-create-meeting"
            >
              {isLoading ? 'Creating...' : '🎥 Create Meeting'}
            </button>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="meeting-features">
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>HD video & audio</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Screen sharing</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Virtual backgrounds</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Chat & reactions</span>
              </div>
            </div>
          </div>
        ) : (
          // Post-meeting creation
          <div className="meeting-ready">
            <div className="icon-container success">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" strokeWidth="2">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <h2>Meeting Ready!</h2>
            <p className="meeting-description">
              Share the link below with others to invite them
            </p>

            <div className="meeting-url-card">
              <div className="url-label">Meeting Link</div>
              <div className="url-text">{meetingUrl}</div>
              {showCopied && (
                <div className="copied-message">
                  ✓ Copied to clipboard!
                </div>
              )}
            </div>

            <div className="meeting-actions">
              <button onClick={copyToClipboard} className="btn-copy">
                📋 Copy Link
              </button>
              <button onClick={joinMeeting} className="btn-join">
                🎥 Join Meeting
              </button>
            </div>

            <div className="meeting-instructions">
              <div className="instruction-header">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="2">
                  <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>How to use</span>
              </div>
              <ol>
                <li>Copy the meeting link using the button above</li>
                <li>Share it with your study partner via email, text, or messenger</li>
                <li>Click "Join Meeting" when you're ready to start</li>
                <li>Your partner can join by clicking the link you shared</li>
              </ol>
            </div>

            <button onClick={() => { setMeetingUrl(''); setMeetingId(''); setJwtToken(''); setShowMeeting(false); }} className="btn-new-meeting">
              Create Another Meeting
            </button>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default VideoMeetingPage;
